/**
 * Adaptateur Supabase du dépôt de devis.
 *
 * Seul endroit qui connaît les noms de colonnes. La couche métier ne voit que
 * `QuoteDetail` et `SaveQuoteCommand` ; le jour où le stockage change, c'est
 * ce fichier qu'on réécrit, pas les règles ni leurs tests.
 */
import type {
  ExecutionContext,
  QuoteDetail,
  QuoteFilter,
  QuoteLine,
  QuoteRepository,
  QuoteStatus,
  QuoteSummary,
  SaveQuoteCommand,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

const SUMMARY_COLUMNS = `
  id, quote_number, title, status, created_at, sent_at,
  total_ht, total_tva, total_ttc, total_cost, margin_amount, margin_percent,
  client:clients(id, first_name, last_name, company_name, email)
`;

const DETAIL_COLUMNS = `
  ${SUMMARY_COLUMNS},
  description, observations,
  site_address_line1, site_postal_code, site_city,
  validity_days, estimated_duration_days,
  applies_reduced_tva, reduced_tva_rate,
  payment_conditions, warranty_text, special_conditions,
  ai_generated, rejection_reason,
  expected_start_on, expected_end_on,
  chantier:projects!fk_projects_quote(id),
  factures:invoices!invoices_quote_id_fkey(id, invoice_number, type, status, total_ht, total_ttc),
  lines:quote_lines(
    id, is_section_header, section_title, designation, unit,
    quantity, unit_cost, unit_price, tva_rate, notes, sort_order
  )
`;

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown): number => Number(v ?? 0);

function toSummary(row: Row): QuoteSummary {
  const client = Array.isArray(row.client) ? row.client[0] : row.client;
  return {
    id: row.id,
    quoteNumber: row.quote_number ?? "",
    title: row.title,
    status: row.status as QuoteStatus,
    createdAt: row.created_at,
    sentAt: row.sent_at ?? null,
    totals: {
      totalHt: num(row.total_ht),
      totalTva: num(row.total_tva),
      totalTtc: num(row.total_ttc),
      totalCost: num(row.total_cost),
      marginAmount: num(row.margin_amount),
      marginPercent: num(row.margin_percent),
    },
    client: client
      ? {
          id: client.id,
          firstName: client.first_name ?? null,
          lastName: client.last_name ?? null,
          companyName: client.company_name ?? null,
          email: client.email ?? null,
        }
      : null,
  };
}

function toLine(row: Row): QuoteLine {
  return {
    id: row.id,
    isSectionHeader: Boolean(row.is_section_header),
    sectionTitle: row.section_title ?? null,
    designation: row.designation ?? "",
    unit: row.unit ?? "u",
    quantity: num(row.quantity),
    unitCost: num(row.unit_cost),
    unitPrice: num(row.unit_price),
    tvaRate: num(row.tva_rate),
    notes: row.notes ?? null,
  };
}

function toDetail(row: Row): QuoteDetail {
  const lines: Row[] = Array.isArray(row.lines) ? row.lines : [];
  return {
    ...toSummary(row),
    description: row.description ?? null,
    observations: row.observations ?? null,
    siteAddressLine1: row.site_address_line1 ?? null,
    sitePostalCode: row.site_postal_code ?? null,
    siteCity: row.site_city ?? null,
    validityDays: num(row.validity_days) || 30,
    estimatedDurationDays: row.estimated_duration_days ?? null,
    appliesReducedTva: Boolean(row.applies_reduced_tva),
    reducedTvaRate: num(row.reduced_tva_rate) || 10,
    paymentConditions: row.payment_conditions ?? null,
    warrantyText: row.warranty_text ?? null,
    specialConditions: row.special_conditions ?? null,
    aiGenerated: Boolean(row.ai_generated),
    rejectionReason: row.rejection_reason ?? null,
    // Relation inverse : PostgREST rend un tableau, que l'index unique de
    // `projects(quote_id)` borne à un élément au plus.
    openedProjectId:
      (Array.isArray(row.chantier) ? row.chantier[0]?.id : row.chantier?.id) ?? null,
    expectedStartOn: row.expected_start_on ? String(row.expected_start_on).slice(0, 10) : null,
    expectedEndOn: row.expected_end_on ? String(row.expected_end_on).slice(0, 10) : null,
    invoices: (Array.isArray(row.factures) ? row.factures : []).map((f: Row) => ({
      id: f.id,
      invoiceNumber: f.invoice_number ?? "",
      type: f.type,
      status: f.status,
      totalHt: num(f.total_ht),
      totalTtc: num(f.total_ttc),
    })),
    // Le tri vient de la base, mais rien ne garantit l'ordre d'une relation
    // imbriquée : on le rétablit ici plutôt que d'afficher un devis mélangé.
    lines: [...lines]
      .sort((a, b) => num(a.sort_order) - num(b.sort_order))
      .map(toLine),
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError(
    "conflict",
    `L'enregistrement a échoué côté base : ${error.message}`,
    { pgCode: error.code },
  );
}

export function createSupabaseQuoteRepository(
  clientFor: DbClientFactory,
): QuoteRepository {
  return {
    async list(ctx: ExecutionContext, filter?: QuoteFilter): Promise<QuoteSummary[]> {
      let query = clientFor(ctx)
        .from("quotes")
        .select(SUMMARY_COLUMNS)
        .order("created_at", { ascending: false });

      if (filter?.status?.length) query = query.in("status", filter.status);
      if (filter?.clientId) query = query.eq("client_id", filter.clientId);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toSummary);
    },

    async findById(ctx: ExecutionContext, quoteId: string): Promise<QuoteDetail | null> {
      const { data, error } = await clientFor(ctx)
        .from("quotes")
        .select(DETAIL_COLUMNS)
        .eq("id", quoteId)
        .maybeSingle();

      if (error) fail(error);
      return data ? toDetail(data) : null;
    },

    async save(ctx: ExecutionContext, command: SaveQuoteCommand): Promise<QuoteDetail> {
      // Une seule transaction côté Postgres : un enchaînement de trois appels
      // pouvait laisser un devis sans lignes, donc à zéro euro.
      const { data: savedId, error } = await clientFor(ctx).rpc("save_quote_with_lines", {
        p_quote_id: command.quoteId ?? null,
        p_header: {
          client_id: command.clientId,
          title: command.title,
          description: command.description,
          site_address_line1: command.siteAddressLine1,
          site_postal_code: command.sitePostalCode,
          site_city: command.siteCity,
          validity_days: command.validityDays,
          estimated_duration_days: command.estimatedDurationDays,
          applies_reduced_tva: command.appliesReducedTva,
          reduced_tva_rate: command.reducedTvaRate,
          attestation_tva: command.attestationTva,
          payment_conditions: command.paymentConditions,
          warranty_text: command.warrantyText,
          special_conditions: command.specialConditions,
          observations: command.observations,
        },
        p_lines: command.lines.map((l) => ({
          is_section_header: l.isSectionHeader,
          section_title: l.sectionTitle,
          designation: l.designation,
          unit: l.unit,
          quantity: l.quantity,
          unit_cost: l.unitCost,
          unit_price: l.unitPrice,
          tva_rate: l.tvaRate,
          notes: l.notes,
        })),
      });

      if (error) fail(error);

      // On relit après écriture : les totaux sont posés par le trigger sur
      // quote_lines, et le numéro de devis par celui sur quotes. Les renvoyer
      // depuis le calcul du domaine afficherait une valeur que la base n'a pas
      // forcément retenue.
      const saved = await this.findById(ctx, savedId as string);
      if (!saved) {
        throw new DomainError(
          "conflict",
          "Le devis a été enregistré mais n'a pas pu être relu.",
          { quoteId: savedId },
        );
      }
      return saved;
    },

    async changeStatus(
      ctx: ExecutionContext,
      quoteId: string,
      status: QuoteStatus,
      rejectionReason?: string | null,
    ): Promise<QuoteDetail> {
      // Accepter n'est pas un changement de statut comme les autres : c'est
      // celui qui ouvre le chantier. Les deux écritures partent ensemble ou
      // pas du tout, d'où la fonction en base plutôt que deux appels ici —
      // un devis accepté sans chantier serait exactement la double saisie
      // qu'on vient de supprimer.
      if (status === "accepted") {
        const { error } = await clientFor(ctx).rpc("accept_quote", {
          p_quote_id: quoteId,
        });
        if (error) fail(error);
      } else {
        const patch: Row = { status };
        if (status === "rejected") {
          patch.rejected_at = new Date().toISOString();
          patch.rejection_reason = rejectionReason ?? null;
        }

        const { error } = await clientFor(ctx)
          .from("quotes")
          .update(patch)
          .eq("id", quoteId);

        if (error) fail(error);
      }

      const updated = await this.findById(ctx, quoteId);
      if (!updated) {
        throw new DomainError("not_found", "Ce devis n'existe pas.", { quoteId });
      }
      return updated;
    },

    async setExpectedDates(
      ctx: ExecutionContext,
      quoteId: string,
      startsOn: string | null,
      endsOn: string | null,
    ): Promise<QuoteDetail> {
      const { error } = await clientFor(ctx)
        .from("quotes")
        .update({ expected_start_on: startsOn, expected_end_on: endsOn })
        .eq("id", quoteId);
      if (error) fail(error);

      const updated = await this.findById(ctx, quoteId);
      if (!updated) {
        throw new DomainError("not_found", "Ce devis n'existe pas.", { quoteId });
      }
      return updated;
    },
  };
}
